import { Card, Table } from 'antd';
import { DataAuditUnitRow, useGetDataAuditUnitsQuery } from 'gql/types';
import { getDateRangeBounds } from 'modules/dashboard/contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { useRecruiterDashboardSettings } from 'modules/dashboard/hooks/useRecruiterDashboardSettings';
import { FC, useCallback, useMemo } from 'react';

import { getUnitTableColumns, UNIT_CSV_EXTRACTORS } from './unitTableColumns';

import { UNIT_CATEGORY_IDS } from '../../constants';
import { useColumnVisibility } from '../../hooks/useColumnVisibility';
import { useUrlParams } from '../../hooks/useUrlParams';
import { ITabViewProps } from '../../types';
import { downloadCsv, makeCsvFilename } from '../../utils/csvUtils';
import ColumnVisibilityPanel from '../ColumnVisibilityPanel';
import DonorDetailsHeader from '../DonorDetailsHeader';
import TableToolbar from '../TableToolbar';

const EMPTY_COLUMN_VISIBILITY: Record<string, boolean> = {};

const UnitTableView: FC<ITabViewProps> = ({
  meta,
  onBack,
  onLoadActiveDonors,
}) => {
  const { table, filters } = useUrlParams();
  const { dataTableId, colVisKey, unitCategoryId } = table;
  const { dateRange, customDateRange, regionIds, locationIds } = filters;

  const { settings } = useRecruiterDashboardSettings();

  const {
    visibleColumns: unitVisibleColumns,
    handleColumnVisibilityChange: handleUnitColumnVisibilityChange,
  } = useColumnVisibility(colVisKey, EMPTY_COLUMN_VISIBILITY);

  const unitDateBounds = useMemo(() => {
    if (!dateRange) return null;
    return getDateRangeBounds(dateRange, customDateRange);
  }, [dateRange, customDateRange]);

  const { data: unitData, loading: unitLoading } = useGetDataAuditUnitsQuery({
    skip: !unitDateBounds,
    variables: {
      input: {
        periodRange: {
          start: unitDateBounds?.start,
          end: unitDateBounds?.end,
        },
        regionIds: regionIds?.length ? regionIds : undefined,
        locationIds: locationIds?.length ? locationIds : undefined,
        woocommerceCategoryId: unitCategoryId,
      },
    },
  });

  const unitRows = useMemo(
    () => unitData?.getDataAuditUnits?.rows ?? [],
    [unitData]
  );

  const allUnitColumns = useMemo(
    () =>
      getUnitTableColumns(unitRows, unitCategoryId, settings?.ffpDblProductIds),
    [unitRows, unitCategoryId, settings?.ffpDblProductIds]
  );

  const unitColumnKeys = useMemo(
    () =>
      allUnitColumns.map((col) => ({
        key: String(col.key),
        label: String(col.title),
      })),
    [allUnitColumns]
  );

  const visibleUnitColumns = useMemo(
    () =>
      allUnitColumns.filter((col) => {
        const k = col.key as string;
        return !(k in unitVisibleColumns) || unitVisibleColumns[k];
      }),
    [allUnitColumns, unitVisibleColumns]
  );

  const exportUnitsCsv = useCallback(() => {
    const ffpDblIds = settings?.ffpDblProductIds ?? [];
    const extractors: Record<string, (r: DataAuditUnitRow) => string> = {
      ...UNIT_CSV_EXTRACTORS,
      ffpSglDbl: (r) =>
        ffpDblIds.includes(r.woocommerceProductId) ? 'DBL' : 'SGL',
    };
    downloadCsv(
      visibleUnitColumns.map((col) => String(col.title ?? col.key)),
      unitRows.map((r) =>
        visibleUnitColumns.map((col) => extractors[String(col.key)]?.(r) ?? '')
      ),
      makeCsvFilename(dataTableId || 'units')
    );
  }, [visibleUnitColumns, unitRows, dataTableId, settings?.ffpDblProductIds]);

  const title =
    unitCategoryId === UNIT_CATEGORY_IDS.FFP ? 'FFP Units' : 'pRBC Units';
  const description = `Individual ${
    unitCategoryId === UNIT_CATEGORY_IDS.FFP ? 'FFP' : 'pRBC'
  } product units within the selected period and filters.`;

  return (
    <div style={{ padding: 0 }}>
      <DonorDetailsHeader
        title={title}
        description={description}
        iconColor={meta.iconColor}
        donorCount={unitRows.length}
        countLabel="units"
        loading={unitLoading}
        onBack={onBack}
        onLoadActiveDonors={onLoadActiveDonors}
      />

      <Card bodyStyle={{ padding: '16px 20px' }}>
        <TableToolbar
          iconColor={meta.iconColor}
          title="Unit List"
          exportDisabled={!unitRows.length}
          onExport={exportUnitsCsv}
        />

        <ColumnVisibilityPanel
          columns={unitColumnKeys}
          visibleColumns={unitVisibleColumns}
          onChange={handleUnitColumnVisibilityChange}
          ghost
        />

        <Table
          rowKey="productId"
          dataSource={unitRows}
          loading={unitLoading}
          scroll={{ x: 900 }}
          pagination={{
            defaultPageSize: 100,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} units`,
          }}
          size="small"
          style={{ marginTop: 8 }}
          columns={visibleUnitColumns}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={visibleUnitColumns.length}>
                <strong>
                  Total Units: {unitRows.length} | Distinct Donors:{' '}
                  {unitData?.getDataAuditUnits?.donorCount ?? 0}
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>
    </div>
  );
};

export default UnitTableView;
